import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Sparkles, ExternalLink, MapPin, Award } from "lucide-react";
import { TESTIMONIALS, BEFORE_AFTER } from "../lib/constants";
import ResponsiveImage from "./ResponsiveImage";

const TOTAL_FACADES = 500;
const SINCE_YEAR = 1999;
const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?q=GMC+R%C3%A9novation+Fa%C3%A7ade+Valenciennes";

// Mapping ville → type de chantier (sous chaque avant/après)
const CHANTIER_BY_CITY = {
  Anzin: "Ravalement complet — enduit projeté",
  "Saint-Saulve": "Rejointoiement briques anciennes",
  Marly: "Imitation pierre + façade ravalée",
};

function AnimatedCount({ to, duration = 1800 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} data-testid="trust-counter">
      {n.toLocaleString("fr-FR")}
    </span>
  );
}

function GoogleStars({ rating = 5 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`gstar-${i + 1}`}
          size={14}
          className={
            i < rating ? "fill-[#FBBC04] text-[#FBBC04]" : "text-neutral-700"
          }
        />
      ))}
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

export default function TrustSection() {
  return (
    <section
      id="confiance"
      data-testid="trust-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#080808] to-[#050505] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <div className="eyebrow mb-4 inline-flex items-center gap-2">
            <Award size={12} />
            <span>Social proof</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Ils nous ont fait
            <br />
            <em className="text-[#FF5A00] not-italic font-normal">confiance</em>
          </h2>
        </motion.div>

        {/* Counter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto mb-16 md:mb-20"
        >
          <div className="rounded-3xl bg-gradient-to-br from-[#FF5A00]/15 via-transparent to-[#FF5A00]/5 border border-[#FF5A00]/30 p-8 md:p-12 text-center">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FF5A00] mb-3 font-semibold">
              + de
            </div>
            <div className="font-display text-7xl md:text-8xl lg:text-9xl text-white leading-none mb-3">
              <AnimatedCount to={TOTAL_FACADES} />
            </div>
            <div className="text-lg md:text-xl text-neutral-300 max-w-xl mx-auto leading-snug">
              façades rénovées dans le{" "}
              <strong className="text-white">Nord (59)</strong> depuis{" "}
              <strong className="text-white">{SINCE_YEAR}</strong>
            </div>
          </div>
        </motion.div>

        {/* Photos chantiers récents */}
        <div className="mb-16 md:mb-20">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h3 className="font-display text-2xl md:text-3xl text-white">
              Nos chantiers récents
            </h3>
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">
              Photos non retouchées
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {BEFORE_AFTER.map((p, i) => {
              const city = p.title.split(" - ").pop() || "Nord (59)";
              const chantier = CHANTIER_BY_CITY[city] || "Ravalement complet";
              return (
                <motion.a
                  key={p.title}
                  href="#realisations"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`trust-beforeafter-${i}`}
                  className="group block rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-[#FF5A00]/50 transition-colors"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <ResponsiveImage
                      src={p.after}
                      alt={`Chantier réalisé — ${city}`}
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 bg-[#FF5A00] px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider text-white font-semibold flex items-center gap-1">
                      <Sparkles size={10} /> Réalisé
                    </span>
                  </div>
                  <div className="p-4 border-t border-white/10">
                    <div className="flex items-center gap-1.5 text-[#FF5A00] text-xs uppercase tracking-[0.15em] font-semibold mb-1">
                      <MapPin size={11} />
                      <span>{city}</span>
                    </div>
                    <div className="text-sm text-white leading-snug">{chantier}</div>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>

        {/* Google Reviews */}
        <div>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-3">
              <GoogleLogo />
              <h3 className="font-display text-2xl md:text-3xl text-white">
                Avis Google
              </h3>
              <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                <Sparkles size={11} className="text-[#FBBC04]" />
                <span className="text-sm font-semibold text-white">5.0</span>
                <GoogleStars />
                <span className="text-xs text-neutral-400">·</span>
                <span className="text-xs text-neutral-400">
                  {TESTIMONIALS.length}+ avis
                </span>
              </div>
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-google-link"
              className="text-sm text-[#FF5A00] hover:text-orange-400 inline-flex items-center gap-1.5 font-semibold"
            >
              Voir tous les avis sur Google <ExternalLink size={12} />
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TESTIMONIALS.slice(0, 5).map((t, i) => (
              <motion.article
                key={t.name + t.city}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`trust-review-${i}`}
                className="glass rounded-2xl p-5 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5A00] to-[#c44600] flex items-center justify-center text-white font-display text-base">
                    {t.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-semibold leading-tight truncate">
                      {t.name}
                    </div>
                    <div className="text-[11px] text-neutral-500 leading-tight">
                      {t.city} · Avis Google
                    </div>
                  </div>
                  <GoogleLogo />
                </div>
                <GoogleStars rating={t.rating} />
                <p className="text-sm text-neutral-300 leading-relaxed mt-3 line-clamp-5">
                  «&nbsp;{t.text}&nbsp;»
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
